<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BlockPalette from './components/BlockPalette.vue';
import BlockCard from './components/BlockCard.vue';
import BlockInspector from './components/BlockInspector.vue';
import MembersManager from './components/MembersManager.vue';
import { memberRestrictionRule, findSectionTemplate } from './sectionTemplates';

const props = defineProps({
  appId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const app = computed(() => store.getters['appBuilder/getApp'](props.appId));
// Publish state is modeled by the `archived` flag (published => not archived),
// matching AppsList's togglePublish.
const isPublished = computed(() => app.value && !app.value.archived);
const metadataFields = computed(
  () => store.getters['appBuilder/getMetadata'].fields
);

const pages = ref([]);
const dataSources = ref([]);
const memberGroups = ref([]);
// App-level workspace: 'build' (pages/blocks editor) or 'members' (app end-users).
const workspaceMode = ref('build');
const membersCount = ref(0);
const activePageId = ref(null);
const blocks = ref([]);
const selectedBlockId = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);

// Canvas mode:
//  - 'edit'    → the drag/drop block editor (config cards)
//  - 'desktop' → a LIVE preview of the real member portal in a full-width iframe
//  - 'mobile'  → the same live preview constrained to a phone-width frame
// Edit cards look identical at any width, so a true device view has to render the
// actual /p/<slug> runtime rather than just resizing the editor.
const canvasMode = ref('edit');
const isEditMode = computed(() => canvasMode.value === 'edit');
const isMobilePreview = computed(() => canvasMode.value === 'mobile');
// Bumped to force the preview iframe to reload (e.g. after inspector edits).
const previewNonce = ref(0);

// Whether a live preview can render: the runtime serves only published
// (non-archived) apps, mirroring the external "Preview" button.
const canPreview = computed(() => !!app.value?.slug && isPublished.value);

const activePage = computed(
  () => pages.value.find(p => p.id === activePageId.value) || null
);

// Real runtime URL for the active page: /p/<slug> for the home page, or
// /p/<slug>/pages/<page-slug> otherwise. The nonce busts the iframe cache.
const previewUrl = computed(() => {
  if (!app.value?.slug) return '';
  const base = `/p/${app.value.slug}`;
  const page = activePage.value;
  const suffix =
    page && !page.is_home && page.slug ? `/pages/${page.slug}` : '';
  return `${base}${suffix}?preview=${previewNonce.value}`;
});

function refreshPreview() {
  previewNonce.value += 1;
}

// Human-readable address shown in the preview chrome (no cache-busting nonce).
const previewAddress = computed(() => {
  const slug = app.value?.slug || '';
  const page = activePage.value;
  const suffix = page && !page.is_home && page.slug ? `/${page.slug}` : '';
  return `/p/${slug}${suffix}`;
});

const selectedBlock = computed(
  () => blocks.value.find(b => b.id === selectedBlockId.value) || null
);

async function loadBlocks(pageId) {
  blocks.value = [];
  selectedBlockId.value = null;
  if (!pageId) return;
  const payload = await store.dispatch('appBuilder/fetchBlocks', {
    appId: props.appId,
    pageId,
  });
  blocks.value = (payload || []).sort((a, b) => a.position - b.position);
}

async function selectPage(pageId) {
  activePageId.value = pageId;
  await loadBlocks(pageId);
}

async function loadEditor() {
  isLoading.value = true;
  try {
    await store.dispatch('appBuilder/fetchApp', { id: props.appId });
    await store.dispatch('appBuilder/fetchMetadata', 'Ticket');
    const [pagePayload, dsPayload] = await Promise.all([
      store.dispatch('appBuilder/fetchPages', props.appId),
      store.dispatch('appBuilder/fetchDataSources', props.appId),
    ]);
    pages.value = (pagePayload || []).sort((a, b) => a.position - b.position);
    dataSources.value = dsPayload || [];
    memberGroups.value = app.value?.member_groups || [];
    membersCount.value = app.value?.meta?.members_count || 0;
    if (pages.value.length) {
      await selectPage(pages.value[0].id);
    }
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

async function addPage() {
  try {
    const page = await store.dispatch('appBuilder/createPage', {
      appId: props.appId,
      title: t('APP_BUILDER.PAGE.DEFAULT_TITLE'),
      slug: `page-${Date.now()}`,
      locale: app.value?.config?.default_locale || 'en',
      page_type: 'list',
      visibility: 'public',
      position: pages.value.length,
    });
    pages.value.push(page);
    await selectPage(page.id);
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  }
}

async function deletePage(page) {
  try {
    await store.dispatch('appBuilder/deletePage', {
      appId: props.appId,
      id: page.id,
    });
    pages.value = pages.value.filter(p => p.id !== page.id);
    if (activePageId.value === page.id) {
      activePageId.value = null;
      blocks.value = [];
      if (pages.value.length) await selectPage(pages.value[0].id);
    }
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.DELETE_ERROR'));
  }
}

async function addBlock(blockDefaults) {
  if (!activePageId.value) {
    await addPage();
  }
  try {
    const block = await store.dispatch('appBuilder/createBlock', {
      appId: props.appId,
      pageId: activePageId.value,
      block_type: blockDefaults.block_type,
      position: blocks.value.length,
      visibility: blockDefaults.visibility,
      config: blockDefaults.config,
      field_mapping: blockDefaults.field_mapping,
      source_filter: blockDefaults.source_filter,
      allowed_group_ids: blockDefaults.allowed_group_ids,
    });
    blocks.value.push(block);
    selectedBlockId.value = block.id;
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  }
}

// Adds the section's detail block to the SAME page as the list (not a separate page), so the
// detail stays part of the section's page (a sidebar entry) rather than a standalone detail page
// that never shows in the nav. The list block keeps no detail_page_slug, so TicketListBlock links
// rows to the current page with ?record_id=, and the runtime swaps to a record view in place.
async function addSectionDetailBlock(template, ds) {
  const detailBlock = await store.dispatch('appBuilder/createBlock', {
    appId: props.appId,
    pageId: activePageId.value,
    block_type: template.detail_block_type,
    position: blocks.value.length,
    visibility: 'public',
    data_source_key: ds.key,
    config: {},
    field_mapping: template.field_mapping,
    source_filter: [],
    allowed_group_ids: [],
  });
  blocks.value.push(detailBlock);
}

// Drops a pre-built "section": creates a member-scoped data source, then a List block bound to it
// with the template's field mappings — turning ~6 manual steps (source -> restrict -> drag block ->
// bind -> map fields) into one action. Everything stays editable in the inspector afterward. Reuses
// the store actions directly (not the createDataSource wrapper, which auto-binds to selectedBlock).
async function addSection(template) {
  if (!template) return;
  if (!activePageId.value) {
    await addPage();
  }
  isSaving.value = true;
  try {
    const ds = await store.dispatch('appBuilder/createDataSource', {
      appId: props.appId,
      key: `${template.keyPrefix}-${dataSources.value.length + 1}`,
      label: template.label,
      source_model: template.source_model,
      source_kind: 'agora_entity',
    });
    dataSources.value.push(ds);
    await store.dispatch('appBuilder/fetchMetadata', template.source_model);

    // Default to the member's own records (the whole point of a section). Editable to company-wide
    // via the inspector's scope toggle.
    const scopedDs = await store.dispatch('appBuilder/updateDataSource', {
      appId: props.appId,
      id: ds.id,
      row_restriction: [memberRestrictionRule('own')],
    });
    const dsIdx = dataSources.value.findIndex(d => d.id === ds.id);
    if (dsIdx !== -1) dataSources.value[dsIdx] = scopedDs;

    const block = await store.dispatch('appBuilder/createBlock', {
      appId: props.appId,
      pageId: activePageId.value,
      block_type: template.block_type,
      position: blocks.value.length,
      visibility: 'public',
      data_source_key: ds.key,
      config: {},
      field_mapping: template.field_mapping,
      source_filter: [],
      allowed_group_ids: [],
    });
    blocks.value.push(block);
    selectedBlockId.value = block.id;

    // Sections with a detail renderer (e.g. Tickets) also get a detail block on the same page so
    // list rows click through to a full record view in place — wired automatically, no builder
    // steps and no separate (sidebar-less) detail page.
    if (template.detail_block_type) {
      await addSectionDetailBlock(template, ds);
    }
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

function onCanvasDrop(e) {
  const sectionRaw = e.dataTransfer.getData('section-data');
  if (sectionRaw) {
    try {
      addSection(findSectionTemplate(JSON.parse(sectionRaw).id));
    } catch {
      // ignore malformed payloads
    }
    return;
  }
  const raw = e.dataTransfer.getData('block-data');
  if (!raw) return;
  try {
    addBlock(JSON.parse(raw));
  } catch {
    // ignore malformed payloads
  }
}

async function persistBlockOrder() {
  await Promise.all(
    blocks.value.map((block, index) =>
      store.dispatch('appBuilder/updateBlock', {
        appId: props.appId,
        pageId: activePageId.value,
        id: block.id,
        position: index,
      })
    )
  );
}

function onReorder() {
  blocks.value.forEach((b, i) => {
    b.position = i;
  });
  persistBlockOrder();
}

async function updateBlock(updated) {
  const idx = blocks.value.findIndex(b => b.id === updated.id);
  if (idx !== -1) blocks.value[idx] = updated;
  isSaving.value = true;
  try {
    const saved = await store.dispatch('appBuilder/updateBlock', {
      appId: props.appId,
      pageId: activePageId.value,
      id: updated.id,
      block_type: updated.block_type,
      data_source_key: updated.data_source_key,
      visibility: updated.visibility,
      config: updated.config,
      field_mapping: updated.field_mapping,
      source_filter: updated.source_filter,
      allowed_group_ids: updated.allowed_group_ids,
    });
    if (idx !== -1) blocks.value[idx] = { ...saved, dynamic: updated.dynamic };
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

// Default key prefix + human label per supported source model. Mirrors the server-side
// source-adapter registry (AppData::Sources::Registry).
const SOURCE_MODEL_DEFAULTS = {
  Ticket: { keyPrefix: 'tickets', label: 'Tickets' },
  Contract: { keyPrefix: 'contracts', label: 'Contracts' },
  ServiceOrder: { keyPrefix: 'service-orders', label: 'Service Orders' },
};

// Creates a new data source (of the chosen entity model) for this app and binds the
// selected block to it, so a builder can go from an unbound block to a working data
// binding in one click. The field catalog is refetched for the chosen model so the
// inspector's draggable fields match. Row scoping is configured via the
// row_restriction toggle (updateDataSource) once the source exists.
async function createDataSource({ source_model: sourceModel = 'Ticket' } = {}) {
  const defaults = SOURCE_MODEL_DEFAULTS[sourceModel] || {
    keyPrefix: 'source',
    label: sourceModel,
  };
  isSaving.value = true;
  try {
    const ds = await store.dispatch('appBuilder/createDataSource', {
      appId: props.appId,
      key: `${defaults.keyPrefix}-${dataSources.value.length + 1}`,
      label: defaults.label,
      source_model: sourceModel,
      source_kind: 'agora_entity',
    });
    dataSources.value.push(ds);
    await store.dispatch('appBuilder/fetchMetadata', sourceModel);
    if (selectedBlock.value) {
      await updateBlock({ ...selectedBlock.value, data_source_key: ds.key });
    }
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

// Persists a data-source change from the inspector (e.g. the row_restriction moat rule)
// and reflects it in the local dataSources list so the toggle state stays in sync.
async function updateDataSource({ id, ...changes }) {
  isSaving.value = true;
  try {
    const saved = await store.dispatch('appBuilder/updateDataSource', {
      appId: props.appId,
      id,
      ...changes,
    });
    const idx = dataSources.value.findIndex(ds => ds.id === id);
    if (idx !== -1) dataSources.value[idx] = saved;
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

async function deleteBlock(block) {
  try {
    await store.dispatch('appBuilder/deleteBlock', {
      appId: props.appId,
      pageId: activePageId.value,
      id: block.id,
    });
    blocks.value = blocks.value.filter(b => b.id !== block.id);
    if (selectedBlockId.value === block.id) selectedBlockId.value = null;
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.DELETE_ERROR'));
  }
}

function goBackToApps() {
  router.push({ name: 'apps_list' });
}

// Opens the live member-facing portal in a new tab so the builder can see the
// actual rendered result (the canvas is a config view, not a pixel preview).
function openPreview() {
  if (!app.value?.slug) return;
  window.open(`/p/${app.value.slug}`, '_blank', 'noopener');
}

async function togglePublish() {
  if (!app.value) return;
  isSaving.value = true;
  try {
    await store.dispatch('appBuilder/updateApp', {
      id: props.appId,
      archived: !app.value.archived,
    });
    useAlert(
      isPublished.value
        ? t('APP_BUILDER.ALERTS.UNPUBLISHED')
        : t('APP_BUILDER.ALERTS.PUBLISHED')
    );
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

// Keep memberGroups (used by BlockInspector's group-visibility picker) and the
// toolbar count badge in sync when the Members workspace mutates them.
function onMemberGroupsChanged(groups) {
  memberGroups.value = groups || [];
}

function onMembersCountChanged(count) {
  membersCount.value = count;
}

onMounted(loadEditor);
</script>

<template>
  <div class="flex flex-col flex-1 w-full min-w-0 h-full overflow-hidden">
    <!-- Editor action toolbar -->
    <div
      class="px-4 py-2 border-b border-n-weak flex items-center gap-3 bg-n-background shrink-0"
    >
      <button
        class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12 rounded-lg px-2 py-1 hover:bg-n-alpha-1 shrink-0"
        @click="goBackToApps"
      >
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('APP_BUILDER.EDITOR.BACK') }}
      </button>
      <span class="w-px h-5 bg-n-weak shrink-0" />
      <span
        class="text-base font-medium text-n-slate-12 truncate max-w-[220px]"
      >
        {{ app ? app.name : t('APP_BUILDER.EDITOR.TITLE') }}
      </span>
      <span
        class="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
        :class="
          isPublished
            ? 'text-emerald-500 bg-emerald-500/10'
            : 'text-n-slate-10 bg-n-alpha-2'
        "
      >
        {{
          isPublished
            ? t('APP_BUILDER.STATUS.PUBLISHED')
            : t('APP_BUILDER.STATUS.DRAFT')
        }}
      </span>

      <!-- App-level workspace switch: design the app (Build) vs manage its end-users (Members) -->
      <div
        class="flex items-center gap-0.5 p-0.5 rounded-lg bg-n-alpha-1 shrink-0 ms-2"
        role="group"
        :aria-label="t('APP_BUILDER.WORKSPACE.LABEL')"
      >
        <button
          type="button"
          :aria-pressed="workspaceMode === 'build'"
          class="flex items-center gap-1.5 px-2.5 h-6 rounded-md text-xs font-medium transition-colors"
          :class="
            workspaceMode === 'build'
              ? 'bg-n-background text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="workspaceMode = 'build'"
        >
          <span class="i-lucide-layout-template w-3.5 h-3.5" />
          {{ t('APP_BUILDER.WORKSPACE.BUILD') }}
        </button>
        <button
          type="button"
          :aria-pressed="workspaceMode === 'members'"
          class="flex items-center gap-1.5 px-2.5 h-6 rounded-md text-xs font-medium transition-colors"
          :class="
            workspaceMode === 'members'
              ? 'bg-n-background text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="workspaceMode = 'members'"
        >
          <span class="i-lucide-users w-3.5 h-3.5" />
          {{ t('APP_BUILDER.WORKSPACE.MEMBERS') }}
          <span
            v-if="membersCount"
            class="text-[10px] px-1 rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ membersCount }}
          </span>
        </button>
      </div>

      <span class="flex-1 text-right text-xs text-n-slate-9 truncate">
        {{ isSaving ? t('APP_BUILDER.SAVING') : t('APP_BUILDER.EDITOR.SAVED') }}
      </span>
      <NextButton
        :label="t('APP_BUILDER.EDITOR.PREVIEW')"
        icon="i-lucide-external-link"
        variant="faded"
        color="slate"
        size="xs"
        :disabled="!app || !app.slug"
        @click="openPreview"
      />
      <NextButton
        :label="
          isPublished
            ? t('APP_BUILDER.EDITOR.UNPUBLISH')
            : t('APP_BUILDER.EDITOR.PUBLISH')
        "
        :icon="isPublished ? 'i-lucide-eye-off' : 'i-lucide-globe'"
        :color="isPublished ? 'amber' : 'blue'"
        size="xs"
        :disabled="!app"
        @click="togglePublish"
      />
    </div>

    <!-- Page tabs row (build workspace only) -->
    <div
      v-if="workspaceMode === 'build'"
      class="px-4 py-1.5 border-b border-n-weak flex items-center gap-1 bg-n-solid-1 shrink-0"
    >
      <div
        class="flex items-center gap-1 flex-1 overflow-x-auto"
        role="tablist"
      >
        <button
          v-for="page in pages"
          :key="page.id"
          role="tab"
          :aria-selected="activePageId === page.id"
          class="group/tab flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors shrink-0"
          :class="
            activePageId === page.id
              ? 'bg-n-background text-n-slate-12 font-medium border-n-weak shadow-sm'
              : 'text-n-slate-11 border-transparent hover:bg-n-alpha-1'
          "
          @click="selectPage(page.id)"
        >
          <svg
            v-if="page.is_home"
            class="w-3.5 h-3.5 shrink-0 text-n-slate-10"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
            :aria-label="t('APP_BUILDER.PAGE.HOME')"
          >
            <path d="M3 12l9-9 9 9M5 10v10h14V10" />
          </svg>
          {{ page.title }}
          <span
            class="opacity-0 group-hover/tab:opacity-100 hover:text-red-400 inline-flex"
            :aria-label="t('APP_BUILDER.PAGE.DELETE')"
            @click.stop="deletePage(page)"
          >
            <svg
              class="w-3 h-3"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        </button>
        <button
          class="px-2 py-1.5 rounded-lg text-sm text-n-slate-10 hover:bg-n-alpha-1 inline-flex"
          :title="t('APP_BUILDER.PAGE.ADD')"
          :aria-label="t('APP_BUILDER.PAGE.ADD')"
          @click="addPage"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      <!-- Canvas mode: edit cards, or a live desktop / mobile preview -->
      <div
        class="flex items-center gap-0.5 p-0.5 rounded-lg bg-n-alpha-1 shrink-0"
        role="group"
        :aria-label="t('APP_BUILDER.EDITOR.DEVICE.LABEL')"
      >
        <button
          type="button"
          :aria-pressed="canvasMode === 'edit'"
          :title="t('APP_BUILDER.EDITOR.DEVICE.EDIT')"
          :aria-label="t('APP_BUILDER.EDITOR.DEVICE.EDIT')"
          class="flex items-center gap-1.5 px-2 h-6 rounded-md text-xs font-medium transition-colors"
          :class="
            canvasMode === 'edit'
              ? 'bg-n-background text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="canvasMode = 'edit'"
        >
          <svg
            class="w-4 h-4 shrink-0"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
          {{ t('APP_BUILDER.EDITOR.DEVICE.EDIT') }}
        </button>
        <button
          type="button"
          :aria-pressed="canvasMode === 'desktop'"
          :title="t('APP_BUILDER.EDITOR.DEVICE.DESKTOP')"
          :aria-label="t('APP_BUILDER.EDITOR.DEVICE.DESKTOP')"
          class="flex items-center justify-center w-7 h-6 rounded-md transition-colors"
          :class="
            canvasMode === 'desktop'
              ? 'bg-n-background text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="canvasMode = 'desktop'"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
          </svg>
        </button>
        <button
          type="button"
          :aria-pressed="canvasMode === 'mobile'"
          :title="t('APP_BUILDER.EDITOR.DEVICE.MOBILE')"
          :aria-label="t('APP_BUILDER.EDITOR.DEVICE.MOBILE')"
          class="flex items-center justify-center w-7 h-6 rounded-md transition-colors"
          :class="
            canvasMode === 'mobile'
              ? 'bg-n-background text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="canvasMode = 'mobile'"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
          >
            <rect x="5" y="2" width="14" height="20" rx="2" />
            <path d="M12 18h.01" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Members workspace: full-width table of the app's end-users -->
    <MembersManager
      v-if="workspaceMode === 'members'"
      :app-id="appId"
      @groups-changed="onMemberGroupsChanged"
      @count-changed="onMembersCountChanged"
    />

    <!-- Three panes (build workspace) -->
    <div v-else class="flex flex-1 overflow-hidden">
      <!-- Left: block palette -->
      <BlockPalette @add-block="addBlock" @add-section="addSection" />

      <!-- Center: vertical canvas (renders as a preview of the member-facing page) -->
      <div
        class="flex-1 overflow-y-auto p-6 bg-n-slate-2"
        @dragover.prevent
        @drop="onCanvasDrop"
      >
        <div v-if="isLoading" class="text-sm text-n-slate-10">
          {{ t('APP_BUILDER.LOADING') }}
        </div>
        <template v-else-if="activePage">
          <!-- EDIT MODE: drag/drop block editor -->
          <div
            v-if="isEditMode"
            class="max-w-2xl mx-auto bg-n-background rounded-xl border border-n-weak shadow-sm overflow-hidden"
          >
            <!-- Faux browser bar so the canvas reads as the published page -->
            <div
              class="px-4 py-2 border-b border-n-weak flex items-center gap-2 text-xs text-n-slate-10 bg-n-solid-1"
            >
              <span class="w-2 h-2 rounded-full bg-n-slate-6 shrink-0" />
              <span class="truncate">{{ previewAddress }}</span>
            </div>
            <div class="p-4">
              <Draggable
                v-model="blocks"
                item-key="id"
                handle=".app-block-drag-handle"
                class="flex flex-col gap-3"
                @end="onReorder"
              >
                <template #item="{ element: block }">
                  <BlockCard
                    :block="block"
                    :data-sources="dataSources"
                    :selected="selectedBlockId === block.id"
                    @select="selectedBlockId = block.id"
                    @delete="deleteBlock(block)"
                  />
                </template>
              </Draggable>

              <div
                v-if="!blocks.length"
                class="py-16 text-center text-sm text-n-slate-9 border-2 border-dashed border-n-weak rounded-xl"
              >
                {{ t('APP_BUILDER.CANVAS.EMPTY') }}
              </div>
              <div
                v-else
                class="mt-3 py-3 text-center text-xs text-n-slate-9 border-2 border-dashed border-n-weak rounded-lg"
              >
                {{ t('APP_BUILDER.CANVAS.ADD_MORE') }}
              </div>
            </div>
          </div>

          <!-- PREVIEW MODE: live render of the real member-facing portal -->
          <div v-else class="h-full flex flex-col items-center">
            <template v-if="canPreview">
              <div
                class="bg-n-background border border-n-weak overflow-hidden flex flex-col transition-all duration-300 ease-out"
                :class="
                  isMobilePreview
                    ? 'w-[390px] h-[780px] max-h-full rounded-[2.25rem] border-[6px] border-n-slate-5 shadow-lg'
                    : 'w-full max-w-5xl h-full rounded-xl shadow-sm'
                "
              >
                <!-- Mobile chrome: speaker notch + address pill + refresh -->
                <div
                  v-if="isMobilePreview"
                  class="relative px-3 pt-3 pb-2.5 bg-n-solid-1 border-b border-n-weak shrink-0"
                >
                  <span
                    class="absolute left-1/2 -translate-x-1/2 top-2 w-14 h-1.5 rounded-full bg-n-slate-6"
                  />
                  <div class="mt-3 flex items-center gap-1.5">
                    <div
                      class="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-1 rounded-full bg-n-alpha-1 text-[11px] text-n-slate-10"
                    >
                      <svg
                        class="w-3 h-3 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        viewBox="0 0 24 24"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                      <span class="truncate">{{ previewAddress }}</span>
                    </div>
                    <button
                      type="button"
                      class="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1"
                      :title="t('APP_BUILDER.EDITOR.DEVICE.REFRESH')"
                      :aria-label="t('APP_BUILDER.EDITOR.DEVICE.REFRESH')"
                      @click="refreshPreview"
                    >
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        viewBox="0 0 24 24"
                      >
                        <path d="M21 2v6h-6" />
                        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                        <path d="M3 22v-6h6" />
                        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                      </svg>
                    </button>
                  </div>
                </div>
                <!-- Desktop chrome: browser bar + refresh -->
                <div
                  v-else
                  class="px-4 py-2 border-b border-n-weak flex items-center gap-2 text-xs text-n-slate-10 bg-n-solid-1 shrink-0"
                >
                  <span class="w-2 h-2 rounded-full bg-n-slate-6 shrink-0" />
                  <span class="truncate flex-1">{{ previewAddress }}</span>
                  <button
                    type="button"
                    class="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1"
                    :title="t('APP_BUILDER.EDITOR.DEVICE.REFRESH')"
                    :aria-label="t('APP_BUILDER.EDITOR.DEVICE.REFRESH')"
                    @click="refreshPreview"
                  >
                    <svg
                      class="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      viewBox="0 0 24 24"
                    >
                      <path d="M21 2v6h-6" />
                      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                      <path d="M3 22v-6h6" />
                      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                    </svg>
                  </button>
                </div>
                <!-- The actual published runtime, framed by device -->
                <iframe
                  :key="previewUrl"
                  :src="previewUrl"
                  :title="t('APP_BUILDER.EDITOR.DEVICE.PREVIEW_FRAME')"
                  class="flex-1 w-full bg-white"
                />
              </div>
            </template>
            <!-- Unpublished apps have no live runtime to render -->
            <div
              v-else
              class="flex flex-col items-center justify-center h-full gap-3 text-center px-6"
            >
              <div
                class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center text-n-slate-10"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20" />
                  <path
                    d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                  />
                </svg>
              </div>
              <p class="text-sm text-n-slate-11 max-w-xs">
                {{ t('APP_BUILDER.PREVIEW.PUBLISH_HINT') }}
              </p>
              <NextButton
                :label="t('APP_BUILDER.EDITOR.PUBLISH')"
                icon="i-lucide-globe"
                color="blue"
                size="sm"
                :disabled="!app"
                @click="togglePublish"
              />
            </div>
          </div>
        </template>
        <div
          v-else
          class="flex flex-col items-center justify-center h-full gap-3"
        >
          <p class="text-sm text-n-slate-10">
            {{ t('APP_BUILDER.CANVAS.NO_PAGE') }}
          </p>
          <NextButton
            :label="t('APP_BUILDER.PAGE.ADD')"
            color="blue"
            size="sm"
            @click="addPage"
          />
        </div>
      </div>

      <!-- Right: block inspector -->
      <BlockInspector
        v-if="selectedBlock"
        :key="selectedBlock.id"
        :block="selectedBlock"
        :data-sources="dataSources"
        :metadata-fields="metadataFields"
        :member-groups="memberGroups"
        @update="updateBlock"
        @update-data-source="updateDataSource"
        @create-data-source="createDataSource"
      />
      <div
        v-else
        class="w-72 border-l border-n-weak bg-n-slate-1 shrink-0 flex flex-col gap-4 p-4 overflow-y-auto"
      >
        <div>
          <span
            class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ t('APP_BUILDER.INSPECTOR.APP_TITLE') }}
          </span>
          <p class="text-sm font-medium text-n-slate-12 mt-1 truncate">
            {{ app ? app.name : t('APP_BUILDER.EDITOR.TITLE') }}
          </p>
          <p class="text-xs text-n-slate-10 truncate">
            {{ `/p/${app ? app.slug : ''}` }}
          </p>
        </div>
        <div class="border-t border-n-weak pt-4">
          <p class="text-xs font-medium text-n-slate-11 mb-2">
            {{ t('APP_BUILDER.INSPECTOR.HELP_TITLE') }}
          </p>
          <ul class="text-xs text-n-slate-10 list-disc ps-4 space-y-1.5">
            <li>{{ t('APP_BUILDER.INSPECTOR.HELP_1') }}</li>
            <li>{{ t('APP_BUILDER.INSPECTOR.HELP_2') }}</li>
            <li>{{ t('APP_BUILDER.INSPECTOR.HELP_3') }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
